import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Search,
  Loader2,
  Building2,
  User,
  Users,
  ArrowRight,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MATCH_ICONS = {
  ospite: User,
  familiare: Users,
  contatto_familiare: Users,
};

export default function RicercaOspiteGlobaleDialog({ open, onOpenChange }) {
  const { token, activeStruttura, switchStruttura, strutture } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setLoading(false);
      setOpeningId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !token) return undefined;

    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/fascicoli-assistenziali/ricerca-globale`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: term, limit: 50 },
        });
        setResults(res.data?.results || []);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.detail || "Errore nella ricerca ospiti");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [query, open, token]);

  const handleOpenResult = useCallback(
    async (result) => {
      if (!result?.fascicolo_id) return;
      setOpeningId(result.fascicolo_id);
      try {
        const needsSwitch =
          result.struttura_id &&
          activeStruttura?.id &&
          result.struttura_id !== activeStruttura.id;

        if (needsSwitch) {
          await switchStruttura(result.struttura_id);
          toast.success(`Struttura attiva: ${result.struttura_nome}`);
        }

        onOpenChange(false);
        navigate(`/fascicoli-assistenziali/${result.fascicolo_id}`);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.detail || "Errore durante l'apertura del fascicolo");
      } finally {
        setOpeningId(null);
      }
    },
    [activeStruttura?.id, navigate, onOpenChange, switchStruttura]
  );

  const multiStruttura = (strutture || []).length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[560px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Search className="w-5 h-5 text-purple-600" />
            Cerca ospite
          </DialogTitle>
          <DialogDescription>
            {multiStruttura
              ? "Ricerca tra tutte le strutture del tuo account: ospite, familiare o contatti."
              : "Cerca per nome, cognome, codice fiscale, telefono, email o contatti familiari."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nome, cognome, CF, telefono, email..."
              className="pl-9 pr-9 bg-white"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Cancella ricerca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-[min(60vh,420px)] overflow-y-auto">
          {query.trim().length < 2 && (
            <p className="text-sm text-slate-500 text-center py-10 px-5">
              Digita almeno 2 caratteri per avviare la ricerca
            </p>
          )}

          {query.trim().length >= 2 && loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="text-sm">Ricerca in corso...</span>
            </div>
          )}

          {query.trim().length >= 2 && !loading && results.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-10 px-5">
              Nessun ospite trovato per &ldquo;{query.trim()}&rdquo;
            </p>
          )}

          {query.trim().length >= 2 && !loading && results.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {results.map((result) => {
                const MatchIcon = MATCH_ICONS[result.match_tipo] || User;
                const isOpening = openingId === result.fascicolo_id;
                const isOtherStruttura =
                  result.struttura_id &&
                  activeStruttura?.id &&
                  result.struttura_id !== activeStruttura.id;

                return (
                  <li key={`${result.fascicolo_id}-${result.match_tipo}-${result.match_label}`}>
                    <button
                      type="button"
                      disabled={Boolean(openingId)}
                      onClick={() => handleOpenResult(result)}
                      className={cn(
                        "w-full text-left px-5 py-3.5 hover:bg-purple-50/60 transition-colors",
                        "focus-visible:outline-none focus-visible:bg-purple-50/60",
                        isOpening && "bg-purple-50/80"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
                          <MatchIcon className="w-4 h-4 text-purple-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-slate-900 truncate">
                              {result.nome_completo}
                            </p>
                            {result.archiviata && (
                              <Badge variant="outline" className="text-[10px] text-slate-500">
                                Archiviato
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-purple-700 mt-0.5">{result.match_label}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
                            {result.codice_fiscale && (
                              <span className="font-mono">{result.codice_fiscale}</span>
                            )}
                            {result.telefono && <span>{result.telefono}</span>}
                            {result.camera && result.camera !== "N/A" && (
                              <span>{result.camera}</span>
                            )}
                          </div>
                          {multiStruttura && (
                            <div className="flex items-center gap-1.5 mt-2">
                              <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <Badge
                                className={cn(
                                  "text-[10px] font-normal",
                                  isOtherStruttura
                                    ? "bg-amber-100 text-amber-800 border border-amber-200"
                                    : "bg-slate-100 text-slate-700"
                                )}
                              >
                                {result.struttura_nome}
                                {isOtherStruttura && " · cambio struttura"}
                              </Badge>
                            </div>
                          )}
                        </div>
                        <div className="shrink-0 self-center text-slate-400">
                          {isOpening ? (
                            <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                          ) : (
                            <ArrowRight className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {query.trim().length >= 2 && !loading && results.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-500 text-center">
            {results.length} risultat{results.length === 1 ? "o" : "i"}
            {multiStruttura && " · clic su un risultato per aprire il fascicolo (cambio struttura automatico se necessario)"}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RicercaOspiteGlobaleButton({ onClick, className }) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn("h-10 shrink-0 px-3 border-slate-200 bg-white whitespace-nowrap", className)}
      onClick={onClick}
      title="Cerca ospite tra tutte le strutture"
      data-testid="btn-ricerca-ospite-globale"
    >
      <Search className="w-4 h-4 mr-2 shrink-0 text-purple-600" />
      Cerca ospite
    </Button>
  );
}
